/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';

import { getProjectsList } from '~src/services/api/project';
import Loader from '~components/atoms/Loader/Loader';

import { useState, useMemo, useEffect, Fragment } from 'react';
import { useMutation } from 'react-query';

import AboveNav from '~components/organisms/AboveNav/AboveNav';
import LeftNav from '~components/organisms/LeftNav/LeftNav';
import RightDisplayHome from '~components/organisms/RightDisplayHome/RightDisplayHome';
import { fadeInKeyframes } from '~src/styles/animations';
import AppContext from '~src/contexts/AppContext';

import { useThemeSwitcher } from 'react-css-theme-switcher';
import { ThemedStyleFn } from '~src/hooks/useStyles';
import { RecentsTable } from '~components/atoms/FileTables/RecentsTable';

import CachedSharesContext, {
  CachedShareCtx,
} from '~src/contexts/CachedSharesContext';
import { FileToViewProvider } from '~src/contexts/FileToViewContext';
import SidebarContext from '~src/contexts/SidebarContext';
import { FileProgressPanel } from '~components/molecules/FileProgressPanel/FileProgressPanel';
import UploadContext, { UploadCtx } from '~src/contexts/UploadContext';
import FileProgressContext, {
  FileProgressCtx,
} from '~src/contexts/FileProgressContext';
import FileVersionsContext, {
  FileVersionsCtx,
} from '~src/contexts/FileVersionsContext';
import CachedPublicTenderContext, {
  CachedPublicTenderCtx,
} from '~src/contexts/CachedPublicTender';

const ipdMiniMediaQ = `@media (device-width : 768px) and (resolution: 163dpi)`;

/** MyProjects page */
const MyProjects = () => {
  const [doGetProjectsList, projectsListMutation] = useMutation(
    getProjectsList
  );

  useEffect(() => {
    doGetProjectsList();
    // doGetSharesList();
  }, []);

  // set the inital App Context to 'normal' for the regular view of the app
  // the 3 states are 'normal', 'time-machine', 'super-user'
  // TODO: replace with query param

  const [appContextState, setAppContextState] = useState('normal');
  const appContextStateChanged = useMemo(
    () => ({ appContextState, setAppContextState }),
    [appContextState, setAppContextState]
  );

  const [fileProgressContext, setFileProgressContext] = useState<
    FileProgressCtx['fileProgressContext']
  >([]);
  const fileProgressChanged = useMemo(
    () => ({ fileProgressContext, setFileProgressContext }),
    [fileProgressContext, setFileProgressContext]
  );

  const [uploadContext, setUploadContext] = useState<
    UploadCtx['uploadContext']
  >([]);
  const uploadContextChanged = useMemo(
    () => ({ uploadContext, setUploadContext }),
    [uploadContext, setUploadContext]
  );

  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const sidebarContextValue = useMemo(
    () => ({ isSidebarMinimized, setIsSidebarMinimized }),
    [isSidebarMinimized, setIsSidebarMinimized]
  );

  const [currentCachedShare, setCurrentCachedShare] = useState<
    CachedShareCtx['currentCachedShare']
  >(null);
  const [cachedSharesContext, setCachedSharesContext] = useState<
    CachedShareCtx['cachedSharesContext']
  >([]);
  const cacheShares = useMemo(
    () => ({
      currentCachedShare,
      setCurrentCachedShare,
      cachedSharesContext,
      setCachedSharesContext,
    }),
    [
      currentCachedShare,
      setCurrentCachedShare,
      cachedSharesContext,
      setCachedSharesContext,
    ]
  );

  const [currentCachedPublicTender, setCurrentCachedPublicTender] = useState<
    CachedPublicTenderCtx['currentCachedPublicTender']
  >(null);
  const [cachedPublicTendersContext, setCachedPublicTendersContext] = useState<
    CachedPublicTenderCtx['cachedPublicTendersContext']
  >([]);

  const cachePublicTenders = useMemo(
    () => ({
      currentCachedPublicTender,
      setCurrentCachedPublicTender,
      cachedPublicTendersContext,
      setCachedPublicTendersContext,
    }),
    [
      currentCachedPublicTender,
      setCurrentCachedPublicTender,
      cachedPublicTendersContext,
      setCachedPublicTendersContext,
    ]
  );

  const [fileVersionsContext, setFileVersionsContext] = useState<
    FileVersionsCtx['fileVersionsContext']
  >({});

  const fileVersionsContextChanged = useMemo(
    () => ({ fileVersionsContext, setFileVersionsContext }),
    [fileVersionsContext, setFileVersionsContext]
  );

  const { switcher, themes } = useThemeSwitcher();

  useEffect(() => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      switcher({ theme: themes.su });
    } else if (window.sessionStorage.getItem('appState') === 'timeMachine') {
      switcher({ theme: themes.dark });
    }
  }, []);

  return (
    <Fragment>
      <div
        css={{
          height: '100%',
          animation: `${fadeInKeyframes} 0.4s ease-in`,
        }}>
        <AppContext.Provider value={appContextStateChanged}>
          <SidebarContext.Provider value={sidebarContextValue}>
            <FileToViewProvider>
              <CachedSharesContext.Provider value={cacheShares}>
                <CachedPublicTenderContext.Provider value={cachePublicTenders}>
                  <UploadContext.Provider value={uploadContextChanged}>
                    <FileProgressContext.Provider value={fileProgressChanged}>
                      <FileVersionsContext.Provider
                        value={fileVersionsContextChanged}>
                        <AboveNav />
                        <div
                          css={{
                            display: 'flex',
                            width: '100%',
                            flexDirection: 'row-reverse',
                            height: 'calc(100vh - 50px)',
                            [ipdMiniMediaQ]: {
                              height: 'calc(100vh - 50px)',
                            },
                          }}>
                          <RightDisplayHome showProjectInfo>
                            <RecentsTable
                              handleRow={() => ({})}
                              setSelectedItems={() => null}
                              // showDivider
                            />
                          </RightDisplayHome>
                          <LeftNav />
                        </div>

                        {fileProgressContext.length > 0 && (
                          <FileProgressPanel />
                        )}
                      </FileVersionsContext.Provider>
                    </FileProgressContext.Provider>
                  </UploadContext.Provider>
                </CachedPublicTenderContext.Provider>
              </CachedSharesContext.Provider>
            </FileToViewProvider>
          </SidebarContext.Provider>
        </AppContext.Provider>
      </div>
      {projectsListMutation.isLoading && <Loader withBackgroundOverlay />}
    </Fragment>
  );
};

export default MyProjects;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    // width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // border: '3px solid blue',
  },
});
